import React, { useState, useCallback, useRef } from "react";
import type { Fold, FoldDirection, Grid } from "../types.ts";
import { useCanvas } from "../hooks/use-canvas.ts";
import { drawBoard, computeLayout } from "../canvas/board-renderer.ts";
import { detectFoldLineHit, type FoldLineHover } from "../canvas/hit-detection.ts";

interface GameBoardProps {
  grid: Grid;
  onFold: (fold: Fold) => void;
  disabled: boolean;
}

export function GameBoard({ grid, onFold, disabled }: GameBoardProps) {
  const [hovered, setHovered] = useState<FoldLineHover | null>(null);
  const [selected, setSelected] = useState<FoldLineHover | null>(null);
  const sizeRef = useRef({ width: 0, height: 0 });

  const canvasRef = useCanvas(
    (ctx, width, height) => {
      sizeRef.current = { width, height };
      drawBoard(ctx, grid, width, height, {
        hoveredFoldLine: hovered,
        selectedFoldLine: selected,
      });
    },
    [grid, hovered, selected]
  );

  const getMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (disabled || selected) return;
      const pos = getMousePos(e);
      if (!pos) return;

      const { cellSize, offsetX, offsetY } = computeLayout(
        grid,
        sizeRef.current.width,
        sizeRef.current.height
      );
      const hit = detectFoldLineHit(
        pos.x,
        pos.y,
        grid,
        offsetX,
        offsetY,
        cellSize
      );
      setHovered(hit);
    },
    [grid, disabled, selected, getMousePos]
  );

  const handleMouseLeave = useCallback(() => {
    if (!selected) setHovered(null);
  }, [selected]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      const pos = getMousePos(e);
      if (!pos) return;

      const { cellSize, offsetX, offsetY } = computeLayout(
        grid,
        sizeRef.current.width,
        sizeRef.current.height
      );

      if (selected) {
        // Already selected a fold line — clicking away deselects
        setSelected(null);
        setHovered(null);
        return;
      }

      const hit = detectFoldLineHit(
        pos.x,
        pos.y,
        grid,
        offsetX,
        offsetY,
        cellSize
      );
      if (hit) {
        setSelected(hit);
        setHovered(null);
      }
    },
    [grid, disabled, selected, getMousePos]
  );

  const handleDirectionClick = useCallback(
    (direction: FoldDirection) => {
      if (!selected) return;
      onFold({ axis: selected.axis, line: selected.line, direction });
      setSelected(null);
      setHovered(null);
    },
    [selected, onFold]
  );

  const handleDismiss = useCallback(() => {
    setSelected(null);
    setHovered(null);
  }, []);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        className="w-full aspect-square max-w-[700px] cursor-pointer"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      />
      {selected && (
        <DirectionPicker
          axis={selected.axis}
          onPick={handleDirectionClick}
          onDismiss={handleDismiss}
        />
      )}
    </div>
  );
}

function DirectionPicker({
  axis,
  onPick,
  onDismiss,
}: {
  axis: "horizontal" | "vertical";
  onPick: (d: FoldDirection) => void;
  onDismiss: () => void;
}) {
  const options: { direction: FoldDirection; label: string; icon: string }[] =
    axis === "horizontal"
      ? [
          { direction: "up", label: "Fold Up", icon: "↑" },
          { direction: "down", label: "Fold Down", icon: "↓" },
        ]
      : [
          { direction: "left", label: "Fold Left", icon: "←" },
          { direction: "right", label: "Fold Right", icon: "→" },
        ];

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
      <div className="flex gap-3">
        {options.map((opt) => (
          <button
            key={opt.direction}
            onClick={() => onPick(opt.direction)}
            className="flex flex-col items-center gap-1 px-6 py-4 bg-slate-800 hover:bg-slate-700 rounded-xl text-white transition-colors border border-slate-600 hover:border-amber-400"
          >
            <span className="text-3xl">{opt.icon}</span>
            <span className="text-sm font-medium">{opt.label}</span>
          </button>
        ))}
        <button
          onClick={onDismiss}
          className="flex flex-col items-center gap-1 px-4 py-4 bg-slate-900 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors border border-slate-700"
        >
          <span className="text-3xl">✕</span>
          <span className="text-sm">Cancel</span>
        </button>
      </div>
    </div>
  );
}
